import { useState, type FormEvent } from 'react';
import { UserCheck } from 'lucide-react';
import { validaIscrizione, type ModuloIscrizione } from '@corsi/modulo.ts';
import { CampiIscrizione, portaAlPrimoErrore } from '../CampiIscrizione';
import { FondoModulo } from '../FondoModulo';
import { useModuloIscrizione } from '../useModuloIscrizione';
import {
  messaggioErrore,
  nomeBiglietti,
  quantiNelModulo,
  registraBiglietto,
  type BigliettoPubblico,
  type EventoPubblico,
} from '../../../lib/eventi/bigliettiPubblici';
import { oggiRoma } from '../../../lib/dataItalia';

type Props = {
  token: string;
  evento: EventoPubblico;
  biglietto: BigliettoPubblico;
  /** I dati di chi ha comprato: se ci sono compare "Il biglietto è per me". */
  datiAcquirente?: Partial<ModuloIscrizione>;
  onFatto: (esito: { nome: string; conConsenso: boolean; bigliettoPdf: boolean; quantiBiglietti: number }) => void;
};

/**
 * Il modulo per registrare chi usera' un biglietto: gli stessi campi del
 * modulo corsi. Chi ha comprato puo' riempirlo con i suoi dati con un clic;
 * per un collega lo compila lui, o gli gira il link del biglietto.
 */
export function ModuloBiglietto({ token, evento, biglietto, datiAcquirente, onFatto }: Props) {
  const { modulo, errori, setErrori, aggiorna, reimposta, trappola, setTrappola } = useModuloIscrizione();
  const [inviando, setInviando] = useState(false);
  const [erroreInvio, setErroreInvio] = useState<string | null>(null);
  const oggi = oggiRoma();

  async function invia(e: FormEvent): Promise<void> {
    e.preventDefault();
    setErroreInvio(null);

    const controllo = validaIscrizione(modulo, oggi, evento.nome);
    if (!controllo.ok) {
      setErrori(controllo.errori);
      setErroreInvio(messaggioErrore('richiesta_non_valida'));
      portaAlPrimoErrore(controllo.errori);
      return;
    }

    setInviando(true);
    const esito = await registraBiglietto(token, modulo, trappola);
    setInviando(false);
    if (esito.ok) {
      onFatto({
        nome: modulo.nome,
        conConsenso: modulo.consensoInformazioni === 'si',
        bigliettoPdf: esito.dati.biglietto_pdf === true,
        quantiBiglietti: quantiNelModulo(biglietto),
      });
      return;
    }
    if (esito.errori) {
      setErrori(esito.errori);
      portaAlPrimoErrore(esito.errori);
    }
    setErroreInvio(messaggioErrore(esito.codice, evento.email_riferimento));
  }

  const perMe = datiAcquirente && (
    <div className="mb-6 flex flex-col gap-3 rounded-xl border border-primary-200 bg-primary-50/70 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
      <p className="text-base text-gray-800">Il biglietto è per te? Riempiamo i dati che abbiamo dall'ordine.</p>
      <button
        type="button"
        onClick={() => reimposta(datiAcquirente)}
        className="inline-flex items-center justify-center gap-2 rounded-full bg-white px-4 py-2 text-sm font-semibold text-primary-700 ring-1 ring-primary-300 transition hover:bg-primary-100"
      >
        <UserCheck className="h-4 w-4" />
        Il biglietto è per me
      </button>
    </div>
  );

  return (
    <form onSubmit={invia} noValidate className="foglio-quadretti mt-6 rounded-2xl px-4 py-6 shadow-2xl ring-1 ring-black/5 sm:px-8 sm:py-8">
      <p className="mb-4 text-sm font-semibold uppercase tracking-wider text-neutral-500">
        Biglietto {biglietto.numero} · {nomeBiglietti(biglietto)} · ordine n. {biglietto.ordine}
      </p>
      <CampiIscrizione modulo={modulo} errori={errori} oggi={oggi} aggiorna={aggiorna} inCima={perMe} />
      <FondoModulo erroreInvio={erroreInvio} inviando={inviando} testo="Registra" trappola={trappola} onTrappola={setTrappola} />
    </form>
  );
}
